import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { ApiError, api, type SessionResponse } from '@/lib/api';
import type { PublicUser, Role } from '@/lib/types';

interface Credentials {
  email: string;
  password: string;
}

export interface RegisterInput extends Credentials {
  firstName: string;
  lastName: string;
  role: Exclude<Role, 'ADMIN'>;
  businessName?: string;
}

export const useAuthStore = defineStore('auth', () => {
  const user = ref<PublicUser | null>(null);
  /**
   * Whether the initial `refresh()` has finished.
   *
   * Route guards must wait for this. On a hard reload the access token is gone
   * (it lives in memory) while the refresh cookie is not, so for a few hundred
   * milliseconds a genuinely signed-in user looks anonymous — and a guard that
   * runs then bounces them to the login page.
   */
  const ready = ref(false);

  const isAuthenticated = computed(() => user.value !== null);
  const role = computed<Role | null>(() => user.value?.role ?? null);
  const isProvider = computed(() => role.value === 'PROVIDER');
  const isAdmin = computed(() => role.value === 'ADMIN');

  function adopt(session: SessionResponse): void {
    api.adoptSession(session);
    user.value = session.user as PublicUser;
  }

  async function login(credentials: Credentials): Promise<void> {
    adopt(await api.post<SessionResponse>('/auth/login', credentials, { skipRefresh: true }));
  }

  async function register(input: RegisterInput): Promise<void> {
    adopt(await api.post<SessionResponse>('/auth/register', input, { skipRefresh: true }));
  }

  async function logout(): Promise<void> {
    try {
      await api.post('/auth/logout', {}, { skipRefresh: true });
    } catch {
      // The session may already be gone server-side. Local state is what matters.
    }
    api.clearSession();
    user.value = null;
  }

  /** Called once at boot, and again by the router when a guard needs certainty. */
  async function restore(): Promise<void> {
    if (ready.value) return;

    if (!api.hasSession) {
      ready.value = true;
      return;
    }

    try {
      // /auth/refresh returns tokens but no user, so identity comes from /auth/me.
      const token = await api.refresh();
      if (token) user.value = (await api.get<{ user: PublicUser }>('/auth/me')).user;
    } catch (err) {
      // A dead session is expected. A 5xx is not, but it must not wedge the app
      // in a permanently un-ready state either.
      if (!(err instanceof ApiError)) throw err;
    } finally {
      ready.value = true;
    }
  }

  /**
   * Re-reads the cached identity from the server.
   *
   * `emailVerified` is the field that goes stale: it changes on a page the user reached from
   * their inbox, not through anything the store did.
   */
  async function refreshUser(): Promise<void> {
    if (!user.value) return;
    user.value = (await api.get<{ user: PublicUser }>('/auth/me')).user;
  }

  /** Replace the cached identity after the user edits their own account. */
  function setUser(next: PublicUser): void {
    user.value = next;
  }

  /** The API client calls this when a refresh fails: drop the user, keep the route. */
  function onSessionExpired(): void {
    user.value = null;
  }

  return {
    user,
    ready,
    isAuthenticated,
    role,
    isProvider,
    isAdmin,
    login,
    register,
    logout,
    restore,
    refreshUser,
    setUser,
    onSessionExpired,
  };
});
